import { useTranslation } from 'react-i18next';
import React, { useEffect } from 'react';
import { AccountBundle } from '../flowMapTypes';
import { buildFlowArrows, buildGraph, hasType } from '../useFlowGraph';
import { computeNeedsSetup } from '../needsSetup';

import type { PendingSetup } from 'routes/routeDetails/quickStart';

// The flow map's nodes, edges and arrows, plus who still has setup to do.
export function useFlowStructure({
  route,
  routeProvider,
  providerId,
  destinations,
  bundles,
  loading,
  reverseRoutesBySite,
  providerCanReceive,
  destinationsWithRules,
  reverseDestinationsWithRules,
  destinationsWithDeviceFilters,
  onNeedsSetupChange,
  selectedAccountId,
  selectedArrowId,
  vertical,
}: {
  route: any;
  routeProvider: any;
  providerId: string;
  destinations: any[];
  bundles: Record<string, AccountBundle>;
  loading: boolean;
  reverseRoutesBySite?: Record<string, string[]>;
  providerCanReceive?: boolean;
  destinationsWithRules?: Set<string>;
  reverseDestinationsWithRules?: Set<string>;
  destinationsWithDeviceFilters?: Set<string>;
  onNeedsSetupChange?: (pendingSetup: PendingSetup) => void;
  selectedAccountId?: string;
  selectedArrowId?: string;
  vertical: boolean;
}) {
  const { t } = useTranslation();
  // Structural graph: every input is either a primitive or identity-stable
  // parent state, so a plain dependency array works. Selection is layered on
  // afterwards so clicking around never rebuilds nodes and edges.
  const routeEnabled = routeProvider?.enabled !== false;
  const structural = React.useMemo(() => {
    const built = buildFlowArrows({
      providerId,
      provider: route?.provider,
      destinations,
      reverseRoutesBySite: reverseRoutesBySite || {},
      providerCanReceive: !!providerCanReceive,
      routeEnabled,
      // undefined while the destination's integration hasn't loaded — the
      // arrow is drawn unavailable but claims no reason for it.
      destinationCanSend: (destId: string) =>
        bundles[destId] ? hasType(bundles[destId].actions, 'pull') : undefined,
      t,
    });

    // Who still has work to do — one shared answer for the node badges and
    // the Quick Start steps. All badges live on nodes in this model.
    const setup = computeNeedsSetup({
      providerId,
      routeProvider,
      bundles,
      destinations,
      arrows: built,
    });
    const { needsSetupAccounts } = setup;

    const graph = buildGraph({
      provider: route?.provider,
      destinations,
      arrows: built,
      bundles,
      destinationsWithRules: destinationsWithRules || new Set<string>(),
      reverseDestinationsWithRules:
        reverseDestinationsWithRules || new Set<string>(),
      destinationsWithDeviceFilters:
        destinationsWithDeviceFilters || new Set<string>(),
      needsSetupAccounts,
      vertical,
      t,
    });
    return { ...graph, ...setup, arrows: built };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    providerId,
    route?.provider,
    routeProvider,
    destinations,
    bundles,
    routeEnabled,
    reverseRoutesBySite,
    providerCanReceive,
    destinationsWithRules,
    reverseDestinationsWithRules,
    destinationsWithDeviceFilters,
    vertical,
    t,
  ]);

  // The Quick Start banner lives on the page above this tab and has no view of
  // the per-account configurations, so report what is still unconfigured.
  const needsSetupCount = loading ? null : structural.needsSetupCount;
  const providerNeedsSetup = structural.providerNeedsSetup;
  const destinationsNeedSetup = structural.destinationsNeedSetup;
  // Nothing loaded at all (no token yet, or it expired) is not a report: with an
  // empty bundle map every account would read as unconfigured and the banner
  // would flip a finished Route to all-warnings.
  const anyBundleLoaded = Object.keys(bundles).length > 0;
  useEffect(() => {
    if (needsSetupCount !== null && anyBundleLoaded)
      onNeedsSetupChange?.({
        total: needsSetupCount,
        provider: providerNeedsSetup,
        destinations: destinationsNeedSetup,
      });
  }, [
    needsSetupCount,
    providerNeedsSetup,
    destinationsNeedSetup,
    anyBundleLoaded,
    onNeedsSetupChange,
  ]);

  const { nodes, edges } = React.useMemo(
    () => ({
      nodes: structural.nodes.map((n: any) =>
        n.id === selectedAccountId
          ? { ...n, data: { ...n.data, isSelected: true } }
          : n,
      ),
      edges: structural.edges.map((e: any) =>
        e.id === selectedArrowId
          ? { ...e, data: { ...e.data, isSelected: true } }
          : e,
      ),
    }),
    [structural, selectedAccountId, selectedArrowId],
  );

  return {
    arrows: structural.arrows,
    nodes,
    edges,
    routeEnabled,
    needsSetup: !loading && anyBundleLoaded && structural.needsSetupCount > 0,
  };
}
